/**
 * A business's look is colours only: six sets plus its own. Each set is an accent (buttons, links), a secondary (the
 * small details) and a page tint, with the ink and the evening ground its previews draw in.
 */
export type LookId = "wine" | "forest" | "midnight" | "ocean" | "terracotta" | "charcoal";

export interface Look {
  id: LookId;
  name: string;
  accent: string;
  brass: string;
  paper: string;
  ink: string;
  dark: string;
}

export const LOOKS: readonly Look[] = [
  { id: "wine", name: "Wine", accent: "#7a2338", brass: "#b0873a", paper: "#fbf6f1", ink: "#2a1c20", dark: "#170f12" },
  { id: "forest", name: "Forest", accent: "#2f5a43", brass: "#c8963e", paper: "#f3f6f0", ink: "#1c2a22", dark: "#0f1612" },
  { id: "midnight", name: "Midnight", accent: "#22305a", brass: "#8aa4e0", paper: "#f2f4f9", ink: "#1a2033", dark: "#0e1220" },
  { id: "ocean", name: "Ocean", accent: "#0d5f78", brass: "#e3a456", paper: "#eff6f7", ink: "#13303a", dark: "#0b161a" },
  { id: "terracotta", name: "Terracotta", accent: "#b4532f", brass: "#3f5a48", paper: "#fbf3ec", ink: "#33201a", dark: "#1a110d" },
  { id: "charcoal", name: "Charcoal", accent: "#2e2e31", brass: "#c49a2c", paper: "#f5f4f1", ink: "#1e1e20", dark: "#121214" },
];

// Why: businesses picked these colours before the redesign, and a new business starts on the first; they still
// read as the same set rather than as their own colours.
const EARLIER: Record<LookId, { accent: string; brass: string; paper: string }> = {
  wine: { accent: "#7a2338", brass: "#b0873a", paper: "#f6f2f2" },
  forest: { accent: "#1f5c45", brass: "#a98b3f", paper: "#f1f5f1" },
  midnight: { accent: "#26375f", brass: "#8b97bd", paper: "#f1f3f7" },
  ocean: { accent: "#0f5e6e", brass: "#4ba3b3", paper: "#eff5f6" },
  terracotta: { accent: "#b0512f", brass: "#d9915e", paper: "#f7f2ee" },
  charcoal: { accent: "#2f2b33", brass: "#918a9a", paper: "#f3f3f4" },
};

export interface Colours {
  accent: string;
  brass: string;
  paper: string;
}

const same = (a: Colours, b: Colours) =>
  a.accent.toLowerCase() === b.accent.toLowerCase() &&
  a.brass.toLowerCase() === b.brass.toLowerCase() &&
  a.paper.toLowerCase() === b.paper.toLowerCase();

/** The set these colours are, or "custom" when they are the business's own. */
export function lookOf(c: Colours): LookId | "custom" {
  return LOOKS.find((l) => same(l, c) || same(EARLIER[l.id], c))?.id ?? "custom";
}

/** What a look is called where it is named. */
export function lookName(c: Colours): string {
  const id = lookOf(c);
  return id === "custom" ? "Your own colours" : (LOOKS.find((l) => l.id === id)?.name ?? "Your own colours");
}

/** The colours a preview draws in: a set's own ink and evening ground, or ones made from the business's accent. */
export function previewVars(c: Colours): Record<string, string> {
  const set = LOOKS.find((l) => l.id === lookOf(c));
  const font = "'Plus Jakarta Sans',system-ui,-apple-system,'Segoe UI',sans-serif";
  return {
    "--pa": c.accent,
    "--pb": c.brass,
    "--pt": c.paper,
    "--pi": set?.ink ?? "#231f20",
    "--pd": set?.dark ?? `color-mix(in srgb, ${c.accent} 16%, #0c0a0b)`,
    "--pft": font,
    "--pfb": font,
  };
}
